<!--
================================================
Composant gérant le formulaire de modification de l'email du compte.

Actuellement l'utilisateur est déconnecté après modification tout simplement car le jwt est invalidé coté backend en cas de modification de l'email. Une piste d'amélioration serait de ne pas déconnecter l'user et de rafraichir le jwt coté client de façon transparente. (link https://gitlab.com/ousmane1/kronocloud/issues/105)

(/fr/cronocloud-inc/user/settings)
================================================
-->


<!------------------------------------------------
+++++++++++++++++++++ HTML +++++++++++++++++++++++
-------------------------------------------------->

<template>
    <div>

        <div class="formSection">

            <div class="spacer">

                <div class="contTitle has-text-left">
                    <h3 class=""><font-awesome-icon class="icon" :icon="['fas', 'at']" />{{$t('EmailDeConnexion')}}</h3>
                </div>

                <form action="" v-on:keyup.enter="validateBeforeSubmit">
                    <div class="field is-horizontal">

                            <div class="control">
                                <p class="help">{{$t('LAdresseAvecLaquelleVousVousConnectez')}}
                                ({{$t('VousDevrezVousReconnecterApresModif')}})</p> <br>

                                <!--<label class="label">{{$t('email')}}</label>-->
                                <input class="input" v-model="email"
                                       autocomplete="username"
                                       v-validate="'required|email|emailAvailabilityCheck'"
                                       data-vv-validate-on="change" id="userSetting_email"
                                       name="userSetting_email" type="email" v-bind:placeholder="$t('email')">
                                <p class="help is-danger has-text-left" id="error_userSetting_email">{{errors.first('userSetting_email') }}</p>
                                <p v-if="showErrorEmailUnchanged" class="help is-warning has-text-left" id="error_userSetting_email_unchanged">{{ $t('AdresseEmailIdentique')}}</p>

                            </div>

                        <!--WTF !? : j'ai ajouté le champs .dummyField input hidden car avec un seul champ sur la page je ne parvenais pas à soumettre le form avec al touche enter ! @link :https://stackoverflow.com/questions/1370021/why-does-forms-with-single-input-field-submit-upon-pressing-enter-key-in-input-->
                        <input type="text" style="display:none" class="dummyField" >

                    </div>
                </form>


            </div>

            <div class="contFoot">
                <div class="field is-grouped">
                    <p class="control">
                        <a  v-on:click="validateBeforeSubmit" id="EmailSubmitBtn"
                            class="button is-primary has-text-white btnValider" v-bind:class="{'is-loading':isSubmiting}">
                            <font-awesome-icon :icon="['fas', 'check']" class="icon"/>
                            {{$t('valider')}}
                        </a>
                    </p>
                </div>
            </div>

        </div>

    </div>
</template>

<!------------------------------------------------
+++++++++++++++++++ JAVASCRIPT++++++++++++++++++++
-------------------------------------------------->


<script>
    import {mapState} from 'vuex'
    import SentryMixin from "../mixins/SentryMixin.js";
    import GlobalErrorNotification from "../mixins/GlobalErrorNotificationMixin.js";
    import InterceptorMixin from "../mixins/InterceptorMixin.js";
    import TrackingMixin from "@/mixins/TrackingMixin";

    export default {
        name: 'UserSettingEmailComp',
        mixins: [SentryMixin, GlobalErrorNotification,InterceptorMixin, TrackingMixin],
        // components: {},
        data: function () {
            return {
                isSubmiting: false ,     // indique si le formulaire est en cours de soumission
                showErrorEmailUnchanged : '', // la visibilité du message d'erreur en cas d'email identique à l'email actuel
            }
        },
        computed:{
            // https://vuex.vuejs.org/guide/forms.html#two-way-computed-property
            // computed props for store :
            ...mapState({
                currentAccountEmail: state => state.user.currentAccountEmail,
                organizationSlug: state => state.organization.slug,
                userSlug: state => state.user.slug

            }),
            //----------------------------------------------
            // vuex 2-way data binding forms https://vuex.vuejs.org/guide/forms.html#two-way-computed-property
            email: {
                get () {return this.$store.state.user.email},
                set (value) {this.$store.commit('UpdateUserEmail', value)}
            },

        },

        methods: {

            /*
             Retourne la disponibilité de l'adresse email
             (via une custom rule veeValidate)

             Cette méthode diffère de SignupComp.validateEmailAvailability dans le sous où elle retourne true si l'adresse email est déjà utilisé mais que c'est celle de l'utilisateur actuellement authentifié.
             */
            validateEmailAccountAvailability() {
                let self = this;
                this.$validator.extend('emailAvailabilityCheck', {

                    getMessage() {
                        // récupération du message d'erreur dans le dictionnaire pour i18n
                        return self.$validator.dictionary.container[self.$validator.locale].custom_validator.signup_email_availability //pas top mais je n'ai pas trouvé mieux ! (inspiré de https://github.com/baianat/vee-validate/issues/745)

                    },
                    validate() {
                        return self.$http
                            .post("public/signup/email-available-check", {
                                email: self.email,
                            })
                            .then(function (response) {
                                self.$store.commit('UpdateGlobalNotificationCompVisibility', false) // masquage d'une éventuelle erreur

                                // on valide si le backend indique que l'adresse est disponible OU que c'est l'adresse de l'utilisateur actuellement authentifié.
                                if (response.data.valid === true || self.email == self.currentAccountEmail) {
                                    return {
                                        valid: true // la prop valid permet de valider le champs
                                    }
                                }
                                else {
                                    return {valid: false}
                                }
                            })
                            .catch(function (error) {
                                // On catch ici sauf ce qui relève des intercepteurs Axios
                                if (self.shouldBeIntercepted(error.response, self.$auth) === false) {
                                    self.logToSentry(self, error, 'error')
                                    let errorCode = '0017'
                                    self.showGlobalErrorNotif(self,errorCode)
                                    self.$mixpanel.track('global error displayed', {"id":errorCode, "detail": "error checking email availability"})
                                  self.$gtag.exception({'description': 'error '+errorCode + ' | error checking email availability', 'fatal': false})
                                  self.$sentry.captureMessage(`Error ${errorCode} > error checking email availability : ${error}`, 'error')

                                }
                            })
                    }

                });
            },

            /*
            * Valide le formulaire avant sa soumission
            */
            validateBeforeSubmit() {

                //  on empêche la soumission du form si l'email est inchangée  (Fait ici car je n'ai pas réussi avec vee-validate)
                if(this.currentAccountEmail == this.email ){
                    this.showErrorEmailUnchanged = true;
                    return
                }

                this.$store.commit('UpdateGlobalNotificationCompVisibility', false); // on masque un éventuel message à la soumission
                this.isSubmiting = true;

                let self = this
                this.$validator.validateAll().then((result) => {
                    if (result) { // si la validation. réussie
                        this.sendFormQuery();
                        this.showErrorEmailUnchanged = false;
                    }
                    else { // si il y a des erreurs...
                        self.$store.commit('UpdateGlobalNotificationComp', {
                            'isVisible': true,
                            'color': 'is-danger',
                            'message': self.$t('leFormulaireContientDesErreurs')
                        });
                        this.isSubmiting = false;
                    }

                })
            },

            /*
            Envoi la requête pour envoyer le formulaire
            */
            sendFormQuery(){

                let self = this;
                return self.$http
                    .patch(`organizations/${self.organizationSlug}/users/${self.userSlug}`, {
                        email: self.email
                        },
                        {headers: {Authorization: self.$auth.header()}}
                        )
                    .then(function (response) {
                      self.$store.commit('UpdateGlobalNotificationCompVisibility', false) // masquage d'une éventuelle erreur

                      if(response.status == 200){
                            self.$store.commit('UpdateGlobalNotificationComp', {
                                'isVisible': true,
                                'color': 'is-success',
                                'message': self.$t('EmailModifiiéeReconenctezVous')
                            });

                          self.ga4GlobalEventTrack('Email modified' , {})


                        self.$mixpanel.track("account email modified");

                            // On déconnecte l'utilisateur car le jwt est modifié coté backend lors du changement de l'email du compte. (UX à améliorer).

                            self.$router.replace({name: 'logout',params:  {'locale':self.$route.params.locale}})

                        }
                    })
                    .catch(function (error) {
                        // On catch ici sauf ce qui relève des intercepteurs Axios
                        if (self.shouldBeIntercepted(error.response, self.$auth) === false) {
                            self.logToSentry(self, error, 'error')
                            let errorCode = '0018'
                            self.showGlobalErrorNotif(self,errorCode)
                            self.$mixpanel.track('global error displayed', {"id": errorCode, "detail": "error in email account edit query"})

                          self.$sentry.captureMessage(`Error ${errorCode} > error in email account edit query : ${error}`, 'error')


                        }
                    }).then(() =>{
                        // toujours exécuté
                        self.isSubmiting = false;
                    })

            },

        },

        // LIFECYCLE HOOKS (https://v2.vuejs.org/v2/guide/instance.html#Lifecycle-Diagram)

        created: function () {
            // activation des validateur custom pour invitation code et email
            this.validateEmailAccountAvailability();

        },

        mounted: function () {
        },
    }
</script>

<!------------------------------------------------
++++++++++++++++++++++ CSS +++++++++++++++++++++++
-------------------------------------------------->

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">



</style>
